import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { ShieldCheck, AlertTriangle, RefreshCw } from 'lucide-react';
import API_BASE from '../../utils/apiBase';

const MANAGER_ROLES = new Set(['admin', 'super_admin', 'branch_manager', 'manager']);
const MAX_SCAN_PAGES = 120; // bounded safety: 3,000 people at 25 per page

/**
 * Quiet, exception-first quality gate for the training management page.
 * Includes applicants and active employees; never edits a staff record,
 * auto-verifies evidence, or changes their readiness.
 */
export default function TrainingIntegrityMonitor({ compact = false, onShowDetails } = {}) {
  const { token, user } = useAuth();
  const permitted = MANAGER_ROLES.has(String(user?.role || '').toLowerCase());
  const [state, setState] = useState({
    loading: false, scanned: 0, problems: [], completed: false, error: '',
  });
  const [runNumber, setRunNumber] = useState(0);

  useEffect(() => {
    if (!token || !permitted) return undefined;
    let cancelled = false;
    const run = async () => {
      setState({ loading: true, scanned: 0, problems: [], completed: false, error: '' });
      let cursor = null;
      let scanned = 0;
      let problems = [];
      const seen = new Set();
      try {
        for (let page = 0; page < MAX_SCAN_PAGES; page += 1) {
          if (cancelled) return;
          const response = await axios.get(`${API_BASE}/training/hub/integrity-monitor`, {
            headers: { Authorization: `Bearer ${token}` },
            params: { limit: 25, ...(cursor ? { cursor } : {}) },
          });
          if (cancelled) return;
          const result = response.data || {};
          scanned += result.employees_scanned || 0;
          problems = problems.concat(result.issues || []);
          if (result.complete || !result.next_cursor) {
            setState({ loading: false, scanned, problems, completed: true, error: '' });
            return;
          }
          if (seen.has(result.next_cursor)) {
            throw new Error('Scan pagination stopped unexpectedly');
          }
          seen.add(result.next_cursor);
          cursor = result.next_cursor;
          setState({ loading: true, scanned, problems, completed: false, error: '' });
        }
        throw new Error('Scan limit reached: some staff records were not checked');
      } catch (error) {
        if (cancelled) return;
        setState({
          loading: false, scanned, problems, completed: false,
          error: error.response?.data?.detail || error.message || 'Unable to complete the integrity scan',
        });
      }
    };
    run();
    return () => { cancelled = true; };
  }, [token, permitted, runNumber]);

  if (!permitted) return null;

  // Keep the read-only integrity scan alive while hiding the long report.
  // Exceptions must still be visible in the normal task-focused view.
  if (compact) {
    if (!state.error && !state.problems.length) return null;
    return (
      <section role={state.error ? 'alert' : 'status'}
        data-testid="training-integrity-compact"
        className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
        <span className="inline-flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          {state.error
            ? 'Training evidence check incomplete — review needed'
            : `${state.problems.length} staff file${state.problems.length === 1 ? '' : 's'} ${state.problems.length === 1 ? 'needs' : 'need'} evidence checks`}
        </span>
        {onShowDetails && (
          <button type="button" onClick={onShowDetails}
            className="min-h-10 rounded-lg border border-amber-300 bg-white px-3 py-2 font-semibold">
            Review details
          </button>
        )}
      </section>
    );
  }

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-4"
      data-testid="workforce-training-integrity-monitor" aria-live="polite">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-teal-700" />
          <div>
            <h2 className="font-semibold text-slate-900">Training evidence integrity</h2>
            <p className="text-xs text-slate-500">Applicants and employees · existing verified evidence · no repeat uploads</p>
          </div>
        </div>
        <button type="button" onClick={() => setRunNumber(number => number + 1)}
          disabled={state.loading}
          className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm disabled:opacity-50">
          <RefreshCw className={`h-4 w-4 ${state.loading ? 'animate-spin' : ''}`} />
          {state.loading ? 'Checking…' : 'Check again'}
        </button>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-slate-700">
        <span>{state.scanned} people checked</span>
        <span>{state.problems.length} staff files needing attention</span>
        {state.completed && !state.problems.length && (
          <span className="font-medium text-emerald-700">No detected evidence-linking discrepancies</span>
        )}
        {state.loading && <span className="text-slate-500">Checking remaining records…</span>}
      </div>
      {!!state.error && <p role="alert" className="mt-2 text-sm text-amber-800">
        Scan incomplete: {state.error}. Do not treat these results as a clean workforce audit.
      </p>}
      {!!state.problems.length && (
        <div className="mt-3 max-h-72 space-y-2 overflow-y-auto">
          {state.problems.map(person => {
            const stage = String(person.person_stage || '').toLowerCase();
            const path = stage.includes('applicant') || stage.includes('onboarding')
              ? `/portal/recruitment/${person.employee_id}?tab=training`
              : `/portal/employees/${person.employee_id}?tab=training`;
            return (
              <div key={person.employee_id} className="flex flex-wrap items-start justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50/40 p-3">
                <div className="flex-1">
                  <p className="flex items-center gap-2 font-medium text-slate-900">
                    <AlertTriangle className="h-4 w-4 text-amber-700" /> {person.employee_name}
                  </p>
                  <p className="text-xs text-slate-500">{person.person_stage || 'Workforce record'}</p>
                  {person.issues.map((issue, idx) => (
                    <p key={`${issue.code}-${issue.record_id || idx}`} className="mt-1 text-xs text-slate-700">
                      {issue.message} {issue.action}
                    </p>
                  ))}
                </div>
                <Link to={path} className="text-sm font-medium text-teal-800 underline">
                  Review staff file
                </Link>
              </div>
            );
          })}
        </div>
      )}
      <p className="mt-3 text-xs text-slate-500">
        Read-only reconciliation, not an automated regulatory certification. Evidence approvals, role requirements and competency sign-offs remain controlled by CareOps.
      </p>
    </section>
  );
}
